import { useEffect, useMemo, useState } from 'react';
import { Box, InputAdornment, SelectChangeEvent, TextField } from '@mui/material';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ApiUser, useApiAccessService } from 'services/api-access.service';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { TablePagination } from 'components/Pagination/TablePagination';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { InPlaceErrorPlaceholder } from 'components/errorpages/InPlaceErrorPlaceholder';
import { NoTokensAvailable } from './NoTokensAvailable';
import { TbIcon } from 'components/Figma';

export function ActiveTokensTable() {
  const intl = useIntl();
  const { getActiveTokens } = useApiAccessService();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTokens = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await getActiveTokens();

        if (error) {
          setError(error.message);
          setUsers([]);
          return;
        }

        if (data) {
          setUsers(data.users);
        }
      } catch (e) {
        console.error('Failed to load tokens.', e);
        setError('Failed to load active tokens.');
      } finally {
        setLoading(false);
      }
    };

    fetchTokens();
  }, [getActiveTokens]);

  const handleRowClick = (userId: string) => {
    const foundUser = users.find(user => user.id === userId);
    navigate(`/api/token/token-management`, {
      state: {
        userId,
        userName: foundUser?.name,
        userEmail: foundUser?.email,
      },
    });
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;

    return users.filter(
      user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, page, rowsPerPage]);

  const totalCount = filteredUsers.length;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(event.target.value as number);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const columns: ColumnDef<ApiUser>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: intl.formatMessage({ id: 'api_access.token.table.name' }),
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: intl.formatMessage({ id: 'api_access.token.table.email' }),
    },
  ];

  const table = useReactTable({
    data: paginatedUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return (
      <Box className="flex justify-center items-center py-8">
        <MetricsLoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return <InPlaceErrorPlaceholder errorDetails={error} compact testId="api-active-tokens-error" />;
  }

  if (!loading && users.length === 0) {
    return <NoTokensAvailable />;
  }

  return (
    <>
      <Box className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder={intl.formatMessage({
            id: 'api_access.token.search_placeholder',
            defaultMessage: 'Search',
          })}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TbIcon name="MagnifyingGlass" size="lg" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <div className="overflow-hidden rounded-lg border">
        <table className="w-full table-fixed">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} className="h-[48px] border-b">
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="border-r px-2 text-left text-sm font-bold last:border-r-0"
                    style={{ minWidth: header.column.id === 'name' ? 150 : 200 }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                className="group/row h-[48px] cursor-pointer border-b hover:bg-denim-50 last:border-b-0"
                onClick={() => handleRowClick(row.original.id)}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className="break-words border-r px-2 text-sm text-text-primary last:border-r-0">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
}
